// The AI services a workspace can connect, and what the server learns about each once a key is connected.

export type ProviderId = 'anthropic' | 'openai' | 'gemini' | 'elevenlabs';
export const PROVIDER_IDS: ProviderId[] = ['anthropic', 'openai', 'gemini', 'elevenlabs'];

/** text: a chat brain. realtime: speech-to-speech. stt/tts: speech to text and back. */
export type ModelKind = 'text' | 'realtime' | 'stt' | 'tts' | 'embedding';

export interface ModelInfo {
  id: string;
  name: string;
  kind: ModelKind;
  vision?: boolean;
  tools?: boolean;
  contextTokens?: number;
  outputTokens?: number;
  /** a sensible default for this kind */
  recommended?: boolean;
  createdAt?: number;
}

export interface VoiceInfo {
  id: string;
  name: string;
  description?: string;
  gender?: string;
  accent?: string;
  language?: string;
  category?: string;
  /** a URL the admin can play (served by our server, which caches provider previews) */
  previewUrl?: string;
  /** which uses it applies to */
  kinds: Array<'realtime' | 'tts'>;
}

export interface ProviderDescriptor {
  id: ProviderId;
  name: string;
  kinds: ModelKind[];
  keyUrl: string;
  keyPrefix?: string;
  blurb: string;
}

export const PROVIDERS: Record<ProviderId, ProviderDescriptor> = {
  anthropic: {
    id: 'anthropic',
    name: 'Anthropic',
    kinds: ['text'],
    keyUrl: 'https://console.anthropic.com/settings/keys',
    keyPrefix: 'sk-ant-',
    blurb: 'Claude as the brain for text chat. For voice, pair it with an OpenAI or ElevenLabs voice.',
  },
  openai: {
    id: 'openai',
    name: 'OpenAI',
    kinds: ['text', 'realtime', 'stt', 'tts', 'embedding'],
    keyUrl: 'https://platform.openai.com/api-keys',
    keyPrefix: 'sk-',
    blurb: 'GPT models for text, the Realtime API for live voice, and speech to text and text to speech.',
  },
  gemini: {
    id: 'gemini',
    name: 'Google Gemini',
    kinds: ['text', 'realtime', 'tts', 'embedding'],
    keyUrl: 'https://aistudio.google.com/apikey',
    blurb: 'Gemini models for text, the Live API for voice and video, and text to speech.',
  },
  elevenlabs: {
    id: 'elevenlabs',
    name: 'ElevenLabs',
    kinds: ['stt', 'tts'],
    keyUrl: 'https://elevenlabs.io/app/settings/api-keys',
    blurb: 'Thousands of voices for speech, Scribe for speech to text, and ElevenLabs Agents.',
  },
};

export type ConnectionStatus = 'ok' | 'invalid' | 'quota' | 'error' | 'unchecked';

export interface ConnectionView {
  id: string;
  provider: ProviderId;
  label: string;
  keyLast4: string;
  status: ConnectionStatus;
  statusDetail: string | null;
  capabilities: ModelKind[];
  checkedAt: number | null;
  createdAt: number;
}
